import type { Theme } from '@mui/material';
import type { IProductReview } from 'src/types/product';

import { sumBy } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';

import Stack from '@mui/material/Stack';
import Rating from '@mui/material/Rating';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { Grid2, useMediaQuery } from '@mui/material';

import { fShortenNumber } from 'src/utils/format-number';

import { Iconify } from 'src/components/iconify';

import { ProductReviewList } from './product-review-list';
import { ProductReviewNewForm } from './product-review-new-form';

// ----------------------------------------------------------------------

type Props = {
  totalRatings?: number;
  totalReviews?: number;
  reviews?: IProductReview[];
  ratings?: { name: string; starCount: number; reviewCount: number }[];
  listingId: string;
  activityId: string;
  entityType: string;
};

export function ProductDetailsReview({
  totalRatings,
  totalReviews,
  ratings = [],
  reviews = [],
  listingId,
  activityId,
  entityType
}: Props) {
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const review = useBoolean();
  const accessTokenJSON = localStorage.getItem('auth') as string;
  const accessToken = JSON.parse(accessTokenJSON)?.accessToken ?? null;

  const total = sumBy(ratings, (star) => star.starCount);

  const renderSummary = () => (
    <Stack spacing={1} sx={{ alignItems: 'center', justifyContent: 'center' }}>
      <Typography variant="h2" fontFamily="Rubik">
        {totalRatings}
      </Typography>

      <Rating
        icon={<Iconify icon="tabler:star-filled" width={downSm ? 16 : 20} />}
        emptyIcon={<Iconify icon="tabler:star-filled" width={downSm ? 16 : 20} />}
        readOnly
        value={totalRatings}
        precision={0.1}
        sx={{ color: 'primary.main' }}
      />

      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        ({fShortenNumber(totalReviews)} yorum)
      </Typography>
    </Stack>
  );

  const renderReviewButton = () => (
    <Stack sx={{ justifyContent: 'flex-end' }}>
      <Button
        size="large"
        variant="contained"
        color="primary"
        onClick={review.onTrue}
        sx={{ minWidth: 0 }}
      >
        <Iconify icon="fluent:comment-add-48-regular" width={34} sx={{ mr: downSm ? 0 : 1 }} />
        {downSm ? '' : 'Bir Değerlendirme Yazın'}
      </Button>
    </Stack>
  );

  return (
    <>
      <Grid2 container spacing={2} sx={{ pt: 2, pl: 5 }}>
        <Grid2 size={{ xs: 10, md: 5 }}>
          <Typography
            variant="h6"
            fontFamily="Rubik"
            fontWeight={500}
            sx={{ color: 'text.secondary', textDecoration: 'underline', mb: 4 }}
          >
            Yorumlar
          </Typography>
        </Grid2>
        <Grid2 size={4} sx={{ display: { xs: 'none', md: 'flex' } }} />
        <Grid2 size={{ xs: 2, md: 3 }}>{renderReviewButton()}</Grid2>
      </Grid2>

      <ProductReviewList reviews={reviews} />
      <Divider sx={{ borderStyle: 'dashed', pt: 4 }} />
      <ProductReviewNewForm
        open={review.value}
        onClose={review.onFalse}
        isLogin={accessToken}
        listingId={listingId}
        activityId={activityId}
        entityType={entityType}
      />
    </>
  );
}
